/* global YT, Vimeo */
const dataAttributeName = 'data-ua-video-player-options';
const defaults = {
  parentSelector: null,
  targetSelector: null,
  classMap: {
    toggle: 'video-player-played',
  },
  videoType: 'youtube',
  videoId: null,
  isAutoplay: false,
  isLoop: false,
  isMuted: false,
  isInline: false,
  playbackControls: true,
  youTubeAPISrc: 'https://www.youtube.com/player_api',
  isYouTubeAPICreated: false,
  vimeoAPISrc: 'https://player.vimeo.com/api/player.js',
  isVimeoAPICreated: false,
};

/**
 * Video player class
 */
export default class UaVideoPlayer {
  constructor(el, options, id) {
    this.collection = [];
    const elements = this._resolveElements(el);

    elements.forEach((element) => this.addToCollection(element, options, id || element.id));

    if (this.collection.length === 0) {
      return false;
    }

    this._init();
    return this;
  }

  _resolveElements(el) {
    if (el instanceof HTMLElement) return [el];
    if (el instanceof Object) return el;
    return Array.from(document.querySelectorAll(el));
  }

  _init() {
    this.collection.forEach(({ $el, options }) => {
      if ($el.$initializedEl) return;

      if (options.videoType === 'youtube') {
        this._loadAPI(options.youTubeAPISrc, 'isYouTubeAPICreated', options);
      } else if (options.videoType === 'vimeo') {
        this._loadAPI(options.vimeoAPISrc, 'isVimeoAPICreated', options);
      }

      // Trigger autoplay if enabled
      if (options.isAutoplay) {
        this._autoplay($el, options);
      }

      // Click listener for the play button
      $el.addEventListener('click', () => this._handleClick($el, options));

      $el.$initializedEl = options;
    });
  }

  _loadAPI(src, apiFlag, options) {
    if (options[apiFlag]) return;

    const existingScript = document.querySelector(`script[src="${src}"]`);
    if (existingScript) {
      existingScript.addEventListener('load', () => {
        options[apiFlag] = true;
      });
      return;
    }

    const scriptTag = document.createElement('script');
    scriptTag.src = src;
    scriptTag.async = true;

    scriptTag.addEventListener('load', () => {
      options[apiFlag] = true;
    });

    const firstScript = document.querySelector('script');
    firstScript.parentNode.insertBefore(scriptTag, firstScript);

    options[apiFlag] = true;
  }

  _loadPlayer($el, options) {
    if (options.videoType === 'vimeo') {
      this._vimeoPlayer($el, options);
    } else if (options.videoType === 'html5') {
      this._html5Player($el, options);
    } else if (options.videoType === 'youtube') {
      this._youTubePlayer($el, options);
    } else {
      console.error(`Unsupported video type: ${options.videoType}`);
    }
  }

  _handleClick($el, options) {
    const parent = document.querySelector(options.parentSelector);
    if (!parent) return;

    parent.classList.toggle(options.classMap.toggle);

    this._loadPlayer($el, options);
  }

  _autoplay($el, options) {
    const parent = document.querySelector(options.parentSelector);
    if (!parent) return;

    if (!parent.classList.contains(options.classMap.toggle)) {
      parent.classList.add(options.classMap.toggle);
    }

    this._loadPlayer($el, options);
  }

  _youTubePlayer($el, options) {
    const initializePlayer = () => {
      const playerOptions = {
        videoId: options.videoId,
        playerVars: {
          origin: window.location.origin,
          autoplay: options.isAutoplay ? 1 : 0,
          loop: options.isLoop ? 1 : 0,
          mute: options.isMuted ? 1 : 0,
          playsinline: options.isInline ? 1 : 0,
          controls: options.playbackControls ? 1 : 0,
          playlist: options.isLoop ? options.videoId : undefined, // Required for loop in YouTube API
        },
        events: {
          onReady: (event) => {
            // Automatically start playing when the player is ready
            event.target.playVideo();
          },
          onError: (event) => {
            console.error('Error playing the video:', event.data);
          },
        },
        ...options.specificAPIOptions,
      };

      new YT.Player(options.targetSelector.slice(1), playerOptions);
    };

    // If YouTube API is not loaded, wait until it's available
    if (typeof YT !== 'undefined' && YT.Player) {
      initializePlayer();
    } else {
      const interval = setInterval(() => {
        if (typeof YT !== 'undefined' && YT.Player) {
          clearInterval(interval);
          initializePlayer();
        }
      }, 100);

      // After 5 seconds, log an error if the API is still not loaded
      setTimeout(() => {
        if (typeof YT === 'undefined' || !YT.Player) {
          console.error('YouTube API is not loaded.');
        }
      }, 5000);
    }
  }

  _vimeoPlayer($el, options) {
    const initializePlayer = () => {
      const playerOptions = {
        id: options.videoId,
        autoplay: options.isAutoplay,
        loop: options.isLoop,
        muted: options.isMuted,
        playsinline: options.isInline,
        controls: options.playbackControls,
        ...options.specificAPIOptions,
      };

      const iframe = document.getElementById(options.targetSelector.slice(1));
      if (typeof Vimeo !== 'undefined') {
        const vimeoPlayer = new Vimeo.Player(iframe, playerOptions);

        vimeoPlayer.on('loaded', () => {
          iframe.style.width = '100%';
          iframe.style.height = '100%';

          if (options.isInline) {
            iframe.setAttribute('playsinline', '');
          } else {
            iframe.removeAttribute('playsinline');
          }
        });
      } else {
        console.error('Vimeo API is not loaded.');
      }
    };

    // If Vimeo API is not loaded, wait until it's available
    if (typeof Vimeo !== 'undefined' && Vimeo.Player) {
      initializePlayer();
    } else {
      const interval = setInterval(() => {
        if (typeof Vimeo !== 'undefined' && Vimeo.Player) {
          clearInterval(interval);
          initializePlayer();
        }
      }, 100);
    }
  }

  _html5Player($el, options) {
    const container = document.querySelector(options.targetSelector);
    if (!container) return;

    const autoplay = options.isAutoplay ? 'autoplay' : '';
    const muted = options.isMuted ? 'muted' : '';
    const loop = options.isLoop ? 'loop' : '';
    const playsInline = options.isInline ? 'playsinline' : '';
    const controls = options.playbackControls ? 'controls' : '';

    // Function to determine MIME type from file extension
    const getMimeType = (videoId) => {
      const extension = videoId.split('.').pop().toLowerCase();
      switch (extension) {
        case 'mp4':
          return 'video/mp4';
        case 'webm':
          return 'video/webm';
        case 'ogv':
          return 'video/ogg';
        case 'mov':
          return 'video/quicktime';
        default:
          console.warn(`Unsupported video format: ${extension}`);
          return 'video/mp4';
      }
    };

    const mimeType = getMimeType(options.videoId);

    container.innerHTML = `
      <video class="pfx-hero-bg-video__item" style="width:100%;height:100%;" ${controls} ${playsInline} ${autoplay} ${muted} ${loop}>
        <source src="${options.videoId}" type="${mimeType}">
        Your browser doesn't support the HTML5 video tag.
      </video>
    `;
  }

  addToCollection(element, options, id) {
    const mergedOptions = {
      ...defaults,
      ...(element.hasAttribute(dataAttributeName) ? JSON.parse(element.getAttribute(dataAttributeName)) : {}),
      ...options,
    };

    this.collection.push({
      $el: element,
      id: id || null,
      options: mergedOptions,
    });
  }

  getItem(identifier) {
    if (typeof identifier === 'number') {
      return this.collection[identifier]?.$initializedEl || null;
    }
    return this.collection.find(({ id }) => id === identifier)?.$initializedEl || null;
  }
}
